/**
 * Google Places API Address Autocomplete Integration
 *
 * Simple, fast implementation for address lookup and form population
 * Uses PlaceAutocompleteElement (new API) to avoid deprecation warnings
 */

export interface AddressComponents {
  street_number?: string;
  route?: string;
  locality?: string; // City
  administrative_area_level_1?: string; // State abbreviation
  administrative_area_level_1_long?: string; // State full name
  postal_code?: string;
  country?: string;
}

export interface AddressConfig {
  apiKey: string;
  restrictToUS?: boolean;
  delayMs?: number;
}

declare global {
  interface Window {
    google: any;
    __addressAutocompleteLoading?: Promise<void>;
  }
}

// US State abbreviation to full name mapping
const US_STATES_MAP: Record<string, string> = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
  'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
  'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
  'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
  'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
  'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
  'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
  'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming',
  'DC': 'District of Columbia'
};

export class AddressAutocomplete {
  private autocomplete: any = null;
  private config: AddressConfig;

  constructor(
    private inputElement: HTMLInputElement,
    config: AddressConfig
  ) {
    this.config = {
      restrictToUS: true,
      delayMs: 300,
      ...config
    };
  }

  /**
   * Initialize the address autocomplete functionality
   */
  async initialize(): Promise<void> {
    try {
      // Load Google Maps API if not already loaded
      await this.loadGoogleMapsAPI();

      // Create autocomplete instance
      await this.createAutocomplete();

      console.log('✅ Address autocomplete initialized');
    } catch (error) {
      console.error('❌ Failed to initialize address autocomplete:', error);
    }
  }

  /**
   * Load Google Maps API script using loading=async (new recommended approach)
   */
  private async loadGoogleMapsAPI(): Promise<void> {
    // Check if already loaded (importLibrary available = new API ready)
    if (window.google?.maps?.importLibrary) {
      return;
    }

    // Check if script is already in the DOM
    const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
    if (existingScript) {
      // Script exists, wait for it to load
      return new Promise((resolve) => {
        const checkLoaded = () => {
          if (window.google?.maps?.importLibrary) {
            resolve();
          } else {
            setTimeout(checkLoaded, 100);
          }
        };
        checkLoaded();
      });
    }

    // Check if loading is already in progress globally
    if (window.__addressAutocompleteLoading) {
      return window.__addressAutocompleteLoading;
    }

    // Create and store the loading promise globally
    window.__addressAutocompleteLoading = new Promise<void>((resolve, reject) => {
      // Check one more time after getting the promise lock
      if (window.google?.maps?.importLibrary) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      // loading=async is the new recommended approach - no callback or libraries param needed
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.config.apiKey}&loading=async`;
      script.async = true;
      script.setAttribute('data-address-autocomplete', 'true');

      script.onload = () => {
        delete window.__addressAutocompleteLoading;
        resolve();
      };

      // Handle errors
      script.onerror = () => {
        delete window.__addressAutocompleteLoading;
        reject(new Error('Failed to load Google Maps API'));
      };

      // Add script to page
      document.head.appendChild(script);
    });

    return window.__addressAutocompleteLoading;
  }

  /**
   * Create the autocomplete instance and bind events.
   * Uses google.maps.places.Autocomplete (loaded via importLibrary) to enhance
   * the existing Webflow input in-place — preserving all form styling.
   * Note: Autocomplete is deprecated for new API keys (March 2025) but remains
   * functional for existing keys. PlaceAutocompleteElement cannot enhance an
   * existing input (it creates its own shadow-DOM widget), so we keep Autocomplete
   * until Google provides a suitable migration path.
   */
  private async createAutocomplete(): Promise<void> {
    if (!window.google?.maps?.importLibrary) {
      throw new Error('Google Maps API not loaded');
    }

    const { Autocomplete } = await (window as any).google.maps.importLibrary('places') as any;

    const options: any = {
      types: ['address'],
      fields: ['address_components', 'formatted_address', 'geometry']
    };

    if (this.config.restrictToUS) {
      options.componentRestrictions = { country: 'us' };
    }

    this.autocomplete = new Autocomplete(this.inputElement, options);

    this.autocomplete.addListener('place_changed', () => {
      this.handlePlaceChanged();
    });
  }

  /**
   * Handle when user selects a place from autocomplete
   */
  private handlePlaceChanged(): void {
    const place = this.autocomplete.getPlace();

    if (!place.address_components) {
      console.warn('No address components found in selected place');
      return;
    }

    const components = this.extractAddressComponents(place.address_components);
    this.populateAddressFields(components);

    console.log('🏠 Address populated:', components);
  }

  /**
   * Extract structured address components from Google Places result
   */
  private extractAddressComponents(addressComponents: any[]): AddressComponents {
    const components: AddressComponents = {};

    for (const component of addressComponents) {
      const type = component.types[0];
      const longName = component.long_name ?? '';
      const shortName = component.short_name ?? '';

      switch (type) {
        case 'street_number':
          components.street_number = longName;
          break;
        case 'route':
          components.route = longName;
          break;
        case 'locality':
          components.locality = longName;
          break;
        case 'administrative_area_level_1':
          components.administrative_area_level_1 = shortName; // Abbreviation (e.g., "CA")
          components.administrative_area_level_1_long = longName; // Full name (e.g., "California")
          break;
        case 'postal_code':
          components.postal_code = longName;
          break;
        case 'country':
          components.country = shortName;
          break;
      }
    }

    return components;
  }

  /**
   * Populate form fields with extracted address components
   */
  private populateAddressFields(components: AddressComponents): void {
    // Build full street address
    const streetAddress = [components.street_number, components.route]
      .filter(Boolean)
      .join(' ')
      .trim();

    // Populate street address
    if (streetAddress) {
      this.setFieldValue('input[name="address_street"], input[data-name="address_street"]', streetAddress);
    }

    // Populate city
    if (components.locality) {
      this.setFieldValue('input[name="address_city"], input[data-name="address_city"]', components.locality);
    }

    // Populate state - handle both select dropdowns and text inputs
    if (components.administrative_area_level_1) {
      this.populateStateField(components.administrative_area_level_1, components.administrative_area_level_1_long);
    }

    // Populate ZIP code
    if (components.postal_code) {
      this.setFieldValue('input[name="address_zip"], input[data-name="address_zip"]', components.postal_code);
    }

    // Trigger change events for existing form tracking
    this.triggerFieldChangeEvents();
  }

  /**
   * Populate state field - handles both select dropdowns and text inputs
   */
  private populateStateField(stateAbbr: string, stateFull?: string): void {
    const stateSelectors = [
      'select[name="address_state"]',
      'select[data-name="address_state"]',
      'select[name="state"]',
      'select[data-name="state"]',
      'input[name="address_state"]',
      'input[data-name="address_state"]'
    ];

    for (const selector of stateSelectors) {
      const field = document.querySelector(selector) as HTMLSelectElement | HTMLInputElement;
      if (!field) continue;

      if (field.tagName.toLowerCase() === 'select') {
        // Handle dropdown - try both abbreviation and full name
        const selectField = field as HTMLSelectElement;
        let optionFound = false;

        // First try abbreviation (CA, NY, etc.)
        for (const option of selectField.options) {
          if (option.value === stateAbbr || option.text === stateAbbr) {
            selectField.value = option.value;
            optionFound = true;
            break;
          }
        }

        // If not found, try full state name (California, New York, etc.)
        if (!optionFound && stateFull) {
          for (const option of selectField.options) {
            if (option.value === stateFull || option.text === stateFull) {
              selectField.value = option.value;
              optionFound = true;
              break;
            }
          }
        }

        // If still not found, try mapped full name from abbreviation
        if (!optionFound) {
          const mappedFullName = US_STATES_MAP[stateAbbr];
          if (mappedFullName) {
            for (const option of selectField.options) {
              if (option.value === mappedFullName || option.text === mappedFullName) {
                selectField.value = option.value;
                optionFound = true;
                break;
              }
            }
          }
        }

        if (optionFound) {
          // Trigger change event for dropdowns
          const changeEvent = new Event('change', { bubbles: true });
          selectField.dispatchEvent(changeEvent);

          console.log('🏠 State dropdown populated:', selectField.value);
          break; // Stop after first successful population
        }
      } else {
        // Handle text input - use full name if available, otherwise abbreviation
        const inputField = field as HTMLInputElement;
        const valueToUse = stateFull || US_STATES_MAP[stateAbbr] || stateAbbr;
        inputField.value = valueToUse;

        // Trigger input event
        const inputEvent = new Event('input', { bubbles: true });
        inputField.dispatchEvent(inputEvent);

        console.log('🏠 State input populated:', valueToUse);
        break; // Stop after first successful population
      }
    }
  }

  /**
   * Set field value and trigger input event
   */
  private setFieldValue(selector: string, value: string): void {
    const field = document.querySelector(selector) as HTMLInputElement;
    if (field) {
      field.value = value;

      // Trigger input event to ensure form tracking picks up the change
      const event = new Event('input', { bubbles: true });
      field.dispatchEvent(event);
    }
  }

  /**
   * Trigger change and blur events on populated fields for form tracking
   */
  private triggerFieldChangeEvents(): void {
    const addressFields = [
      'input[name="address_street"], input[data-name="address_street"]',
      'input[name="address_city"], input[data-name="address_city"]',
      'input[name="address_state"], input[data-name="address_state"]',
      'input[name="address_zip"], input[data-name="address_zip"]'
    ];

    for (const selector of addressFields) {
      const field = document.querySelector(selector) as HTMLInputElement;
      if (field && field.value) {
        // Trigger blur event for lead tracking
        const blurEvent = new Event('blur', { bubbles: true });
        field.dispatchEvent(blurEvent);

        // Trigger change event
        const changeEvent = new Event('change', { bubbles: true });
        field.dispatchEvent(changeEvent);
      }
    }
  }

  /**
   * Cleanup autocomplete instance
   */
  destroy(): void {
    if (this.autocomplete) {
      window.google.maps.event.clearInstanceListeners(this.autocomplete);
      this.autocomplete = null;
    }
  }
}

/**
 * Initialize address autocomplete for a specific input field
 */
export function initializeAddressAutocomplete(
  inputElement: HTMLInputElement,
  config: AddressConfig
): AddressAutocomplete {
  const autocomplete = new AddressAutocomplete(inputElement, config);
  autocomplete.initialize();
  return autocomplete;
}

/**
 * Auto-detect and initialize address autocomplete on address fields
 */
export function autoInitializeAddressFields(apiKey: string): void {
  // Look for ONLY the main address/street input fields (not city, state, zip)
  const addressFields = document.querySelectorAll(
    'input[name="address_street"], input[data-name="address_street"], input[name="street"], input[data-name="street"]'
  ) as NodeListOf<HTMLInputElement>;

  for (const field of addressFields) {
    try {
      initializeAddressAutocomplete(field, { apiKey });
      console.log('🏠 Address autocomplete initialized for field:', field.name || field.id);
    } catch (error) {
      console.warn('Failed to initialize address autocomplete for field:', field.name || field.id, error);
    }
  }
}
